import type { SocialProviderType } from "lib/auth/authClient";

// Search-param parsing for the better-auth /auth/* routes. In its own file so the sanitisation
// logic is testable.
//
// These params carry data marketing (stotles.com) puts on links into the app, e.g.
//   /auth/register?utm_source=…&partner_code=…&invitation=…&redirect=/bids
// The auth-service is same-origin here, so params survive end-to-end — no `stotles_utm` cookie /
// OAuth-session workaround needed like on the Auth0 path.

// Accept only internal same-origin paths. Rejects absolute (`https://evil.com`), protocol-relative
// (`//evil.com`) and backslash (`/\evil.com`) URLs so a captured destination can't be an open redirect.
const INTERNAL_PATH = /^\/(?![/\\])/;

/**
 * Whether an already signed-in user can skip the login form and go straight to `redirect`. True for
 * no redirect (home) or a relative path to an ordinary page. False for absolute URLs, `/auth/*` pages
 * and the auth-service (`/api/auth/*`). A `redirect` back into the auth-service means it sent the user
 * here to sign in as part of one of its own flows (e.g. an OIDC `prompt=login`), so it needs the
 * sign-in to actually happen rather than be skipped.
 */
export function canSkipLoginFor(redirect: string | undefined): boolean {
  if (redirect === undefined) return true;
  return (
    INTERNAL_PATH.test(redirect) &&
    !redirect.startsWith("/auth/") &&
    !redirect.startsWith("/api/auth/")
  );
}

/**
 * A post-login destination we're willing to navigate to: a relative path, or an absolute URL on our
 * own origin or the auth-service's. The absolute forms exist for the paused OIDC authorization request
 * the auth-service bounces through here, which is cross-origin in local dev.
 */
export function internalPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (INTERNAL_PATH.test(value)) return value;
  return isTrustedAbsoluteUrl(value) ? value : undefined;
}

function isTrustedAbsoluteUrl(value: string): boolean {
  try {
    // No base URL: a protocol-relative or relative value must not resolve against our own origin and
    // pass as trusted.
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    const authServiceOrigin = window.authServiceBaseUrl
      ? new URL(window.authServiceBaseUrl).origin
      : undefined;
    return url.origin === window.location.origin || url.origin === authServiceOrigin;
  } catch {
    return false;
  }
}

// Per-param length cap. Signup forwards ~11 of these on the URL at once, so the cap keeps the whole
// query string under CloudFront's ~8KB request-URL limit. 128 fits real utm/name/company/code
// values with room to spare (worst case ≈11×128 ≈ 1.4KB).
const MAX_TEXT_PARAM_LENGTH = 128;

/**
 * A bounded free-text param (utm tags, partner/invitation codes, name/company overrides). Only
 * length is checked here; semantic validation (does this code/invitation exist) happens server-side.
 * @param value
 * @returns
 */
function textParam(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= MAX_TEXT_PARAM_LENGTH
    ? value
    : undefined;
}

// A Stotles page URL captured by the SEO link decorator (utm_fp / utm_lp). Real SEO URLs -
// /tenders/search/<slug>?stage=OPEN&buyer_location=london&sort=publish_date - run past
// MAX_TEXT_PARAM_LENGTH, and textParam drops rather than truncates, so the long-tail pages these
// are most informative about would silently lose them. Stored and reported only, never navigated to.
const MAX_URL_PARAM_LENGTH = 512;

function stotlesUrlParam(value: unknown): string | undefined {
  if (typeof value !== "string" || !value || value.length > MAX_URL_PARAM_LENGTH) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return undefined;
    return url.hostname === "stotles.com" || url.hostname.endsWith(".stotles.com")
      ? value
      : undefined;
  } catch {
    return undefined;
  }
}

// The IdP's own human-readable error text (OAuth2 `error_description`), passed through by
// better-auth. Longer than a normal text param — Entra's AADSTS messages run ~200 chars — so it gets
// its own cap. Purely informational (shown as secondary copy / logged), never used for control flow.
const MAX_ERROR_DESCRIPTION_LENGTH = 512;

function errorDescriptionParam(value: unknown): string | undefined {
  return typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_ERROR_DESCRIPTION_LENGTH
    ? value
    : undefined;
}

// Matches the cap the auth-service applies when it appends these (oauth-redirect-uri-mismatch.ts).
const MAX_URI_PARAM_LENGTH = 2048;

function uriParam(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= MAX_URI_PARAM_LENGTH
    ? value
    : undefined;
}

// The SEO page types a signup can come from. Keep in sync with crmData.seo_params.dataType in
// nodejs/lib/data/db/schemas/public/user.schema.ts, which rejects any other value.
export const SEO_DATA_TYPES = ["record", "buyer", "supplier", "framework", "topic"] as const;
export type SeoDataType = (typeof SEO_DATA_TYPES)[number];

function seoDataType(value: unknown): SeoDataType | undefined {
  return SEO_DATA_TYPES.find((dataType) => dataType === value);
}

export type LoginSearch = AttributionSearch & {
  // Post-login destination (e.g. /auth/login?redirect=/bids). Optional so a plain
  // `<Link to="/auth/login">` works without search params.
  redirect?: string;
  // Pre-fills the email field, forwarded from the register page when SSO is required.
  email?: string;
  // Starts this provider's sign-in as soon as the page loads, so a link from stotles.com can go
  // straight to Google / Microsoft (e.g. /auth/login?provider=google&utm_source=…).
  provider?: SocialProviderType;
};

// Only providers whose sign-in page is fixed (Google, Microsoft). Never enterprise SSO: a customer
// admin sets its IdP URL, so a link naming one would be an open redirect from a stotles.com link.
const SOCIAL_PROVIDERS: SocialProviderType[] = ["google", "microsoft"];

function socialProvider(value: unknown): SocialProviderType | undefined {
  return SOCIAL_PROVIDERS.find((provider) => provider === value);
}

// TODO: Replace these hand-written type + validator pairs with zod schemas (single source of truth
// via z.infer) and shared primitives. Use per-field `.catch(undefined)` to keep the drop-invalid,
// never-throw semantics. Do as a dedicated PR that also migrates the other validateSearch routes.
export function validateLoginSearch(search: Record<string, unknown>): LoginSearch {
  // Every key is returned, even when undefined: the router spreads this over the raw query, so an
  // omitted key would let an invalid raw value (e.g. an external `redirect`) through.
  return {
    // Attribution is kept because the Google / Microsoft buttons here sign up a new user too.
    ...validateAttributionSearch(search),
    redirect: internalPath(search.redirect),
    email: textParam(search.email),
    provider: socialProvider(search.provider),
  };
}

export type ForgotPasswordSearch = {
  // Pre-fills the email field, forwarded from the login page so the user doesn't retype it.
  email?: string;
};

export function validateForgotPasswordSearch(
  search: Record<string, unknown>,
): ForgotPasswordSearch {
  const email = textParam(search.email);
  return email ? { email } : {};
}

/**
 * Signup/pre-auth context passed via the URL, all optional. Mirrors what
 * `Users::NewAccountController` reads from the `stotles_utm` cookie / OAuth session on the Auth0
 * path; here they arrive as plain query params. `redirect` is the post-verification destination.
 */
export type CallbackSearch = {
  // Post-sign-in destination, threaded through the OAuth round-trip by signInWithProvider.
  redirect?: string;
  // Error code better-auth appends on a failed social/SSO sign-in (e.g. our work-email block, or a
  // provider-forwarded OAuth error). See BetterAuthCallbackPage for the code → message mapping.
  error?: string;
  // The IdP's own error text, forwarded by better-auth alongside `error`. Surfaced as secondary copy
  // (and logged) for org-policy / transient failures where it helps an admin diagnose the setup.
  error_description?: string;
  // Diagnostic params appended by the auth-service redirect_uri mismatch hook. Shown in the technical
  // details block so you can find the rejected redirect_uri without using devtools or checking the logs.
  requested_redirect_uri?: string;
  client_id?: string;
};

export function validateCallbackSearch(search: Record<string, unknown>): CallbackSearch {
  return {
    redirect: internalPath(search.redirect),
    error: textParam(search.error),
    error_description: errorDescriptionParam(search.error_description),
    requested_redirect_uri: uriParam(search.requested_redirect_uri),
    client_id: uriParam(search.client_id),
  };
}

export type EmailConfirmedSearch = {
  // The address the link was issued for, so a failed link can offer a resend without re-asking.
  email?: string;
  // Where to forward the user on success (they arrive signed in via autoSignInAfterVerification).
  redirect?: string;
  // better-auth appends this when it can't honour the link (e.g. TOKEN_EXPIRED). Its absence is what
  // marks the success case, so it drives the whole page.
  error?: string;
};

export function validateEmailConfirmedSearch(
  search: Record<string, unknown>,
): EmailConfirmedSearch {
  return {
    email: textParam(search.email),
    redirect: internalPath(search.redirect),
    error: textParam(search.error),
  };
}

export type EmailChangeSearch = {
  // Set when a link didn't go through (e.g. TOKEN_EXPIRED, EMAIL_OUTSIDE_COMPANY). Success is never
  // read from the URL — it comes from a cookie the auth-service sets — so a crafted link can only
  // ever show an error.
  error?: string;
};

export function validateEmailChangeSearch(search: Record<string, unknown>): EmailChangeSearch {
  return { error: textParam(search.error) };
}

export type VerifyEmailSearch = {
  // The address the confirmation link was sent to, so the page can display it and drive the resend.
  // Set by the auth-service when a social sign-in's provider didn't confirm the email, at which
  // point there's no session to read it from.
  email?: string;
};

export function validateVerifyEmailSearch(search: Record<string, unknown>): VerifyEmailSearch {
  const email = textParam(search.email);
  return email ? { email } : {};
}

export function validateTwoFactorSearch(search: Record<string, unknown>): { redirect?: string } {
  // Post-verification destination, forwarded from the login page or the auth-service social 2FA
  // redirect. Always returned so an invalid raw value can't survive (see validateLoginSearch).
  return { redirect: internalPath(search.redirect) };
}

export type ConsentSearch = {
  client_id?: string;
  scope?: string;
  // OIDC "claims request": individual claims the client wants on top of its scopes. Accepting them
  // changes what /userinfo returns, so the page has to show them. A JSON object on the URL.
  claims?: string;
  // Where the code will be sent. Already matched against the client's registered list before the
  // auth-service paused for consent; read only to warn when it is a listener on the user's machine.
  redirect_uri?: string;
};

// A JSON document, not a word: far more than a real request needs, still inside the URL limit.
const MAX_CLAIMS_LENGTH = 2048;

function claimsParam(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= MAX_CLAIMS_LENGTH
    ? value
    : undefined;
}

/**
 * The OIDC consent page only needs enough to render "app X wants Y".
 *
 * The auth-service also puts its signed authorization query on this URL (`sig`, `exp`, `ba_iat` and
 * a repeated `ba_param` entry per signed param). Those are deliberately *not* captured here: the page
 * reads them straight off `window.location.search` and posts them back verbatim, because
 * re-serialising a repeated param would break the signature. See BetterAuthConsentPage.
 */
export function validateConsentSearch(search: Record<string, unknown>): ConsentSearch {
  const clientId = textParam(search.client_id);
  const scope = textParam(search.scope);
  const claims = claimsParam(search.claims);
  const redirectUri = uriParam(search.redirect_uri);
  return {
    ...(clientId && { client_id: clientId }),
    ...(scope && { scope }),
    ...(claims && { claims }),
    ...(redirectUri && { redirect_uri: redirectUri }),
  };
}

type ResetPasswordSearch = {
  token?: string;
};

// Opaque, single-use token — not free-text marketing input, so it gets its own generous cap rather
// than the MAX_TEXT_PARAM_LENGTH bound used for utm/name params.
const MAX_TOKEN_LENGTH = 512;

export function validateResetPasswordSearch(search: Record<string, unknown>): ResetPasswordSearch {
  const token =
    typeof search.token === "string" &&
    search.token.length > 0 &&
    search.token.length <= MAX_TOKEN_LENGTH
      ? search.token
      : undefined;
  // One-time reset token better-auth appends to the emailed link after validating it.
  return token ? { token } : {};
}

export type SignupSearch = AttributionSearch & {
  redirect?: string;
  // Pre-fills the email field, forwarded from the login page so the user doesn't retype it.
  email?: string;
  // Signup context — resolved server-side (partner programme, invitation/inviter, company match).
  partner_code?: string;
  invitation?: string;
  company?: string;
  first_name?: string;
  last_name?: string;
  // Starts this provider's sign-up on load; see LoginSearch.provider.
  provider?: SocialProviderType;
};

export function validateSignupSearch(search: Record<string, unknown>): SignupSearch {
  return {
    ...validateAttributionSearch(search),
    redirect: internalPath(search.redirect),
    email: textParam(search.email),
    partner_code: textParam(search.partner_code),
    invitation: textParam(search.invitation),
    company: textParam(search.company),
    first_name: textParam(search.first_name),
    last_name: textParam(search.last_name),
    provider: socialProvider(search.provider),
  };
}

// Marketing attribution put on links into the app, stored on a new user at signup (crmData).
export type AttributionSearch = {
  // Attribution (utm_* + gclid) — stored on the user at signup (crmData.signup_utm).
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  utm_adgroup?: string;
  utm_referrer?: string;
  gclid?: string;
  // iOS click IDs. Google sends these instead of gclid when it cannot set a cookie.
  gbraid?: string;
  wbraid?: string;
  // Our own source fallback, only read when the landing URL carried no utm_source.
  utm_source_stotles?: string;
  // First page of the session and the page the CTA was clicked on, from the SEO decorator.
  utm_fp?: string;
  utm_lp?: string;
  // SEO context (mirrors crmData.seo_params { id, dataType }).
  seo_id?: string;
  seo_data_type?: SeoDataType;
};

export function validateAttributionSearch(search: Record<string, unknown>): AttributionSearch {
  return {
    utm_source: textParam(search.utm_source),
    utm_medium: textParam(search.utm_medium),
    utm_campaign: textParam(search.utm_campaign),
    utm_term: textParam(search.utm_term),
    utm_content: textParam(search.utm_content),
    utm_id: textParam(search.utm_id),
    utm_adgroup: textParam(search.utm_adgroup),
    utm_referrer: textParam(search.utm_referrer),
    gclid: textParam(search.gclid),
    gbraid: textParam(search.gbraid),
    wbraid: textParam(search.wbraid),
    utm_source_stotles: textParam(search.utm_source_stotles),
    utm_fp: stotlesUrlParam(search.utm_fp),
    utm_lp: stotlesUrlParam(search.utm_lp),
    seo_id: textParam(search.seo_id),
    seo_data_type: seoDataType(search.seo_data_type),
  };
}
